/* ==========================================================================
   JADUZAR INK — body selector

   The list of areas is the real control. The drawing mirrors it: clicking a
   part of the figure selects the matching list entry, and the panel shows
   that entry. No dependencies; without this file the list is plain links.
   ========================================================================== */
(() => {
  'use strict';

  /* An entry can light up several shapes at once. */
  const GROUPS = { front: ['chest', 'stomach', 'ribs'] };

  const setup = (root) => {
    if (root.dataset.jiBodyReady === 'true') return;
    root.dataset.jiBodyReady = 'true';

    const picks = Array.from(root.querySelectorAll('[data-ji-body-pick]'));
    const panels = Array.from(root.querySelectorAll('[data-ji-body-panel]'));
    const regions = Array.from(root.querySelectorAll('.ji-bodysel__region'));
    if (!picks.length) return;

    /* Which entry does a shape belong to? A direct match wins; a group such
       as "Full front" only claims shapes that no single area has claimed. */
    const owner = new Map();
    picks.forEach((p) => {
      const r = p.dataset.region;
      if (r && r !== 'none' && !GROUPS[r] && !owner.has(r)) owner.set(r, p);
    });
    picks.forEach((p) => {
      (GROUPS[p.dataset.region] || []).forEach((r) => {
        if (!owner.has(r)) owner.set(r, p);
      });
    });

    /* A shape nobody owns is not clickable — it is just part of the body. */
    regions.forEach((el) => el.classList.toggle('is-off', !owner.has(el.dataset.region)));

    const shapesOf = (pick) => {
      const r = pick.dataset.region;
      const names = GROUPS[r] || [r];
      return regions.filter((el) => names.indexOf(el.dataset.region) !== -1);
    };

    let current = null;

    const select = (pick, reveal) => {
      if (!pick || pick === current) return;
      current = pick;

      picks.forEach((p) => {
        if (p === pick) p.setAttribute('aria-current', 'true');
        else p.removeAttribute('aria-current');
      });

      const active = shapesOf(pick);
      regions.forEach((el) => el.classList.toggle('is-active', active.indexOf(el) !== -1));

      let shown = null;
      panels.forEach((panel) => {
        const on = panel.dataset.jiBodyPanel === pick.dataset.jiBodyPick;
        panel.hidden = !on;
        panel.classList.toggle('is-in', on);
        if (on) shown = panel;
      });

      /* On a phone the panel can be off screen when an area is tapped. Bring
         it into view only then, and only as far as needed. */
      if (reveal && shown) {
        const box = shown.getBoundingClientRect();
        if (box.top < 0 || box.bottom > window.innerHeight) {
          shown.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    };

    picks.forEach((pick) => {
      pick.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        select(pick, true);
      });
    });

    const hover = (region, on) => {
      const pick = owner.get(region);
      if (!pick) return;
      shapesOf(pick).forEach((el) => el.classList.toggle('is-hover', on));
      pick.classList.toggle('is-hover', on);
    };

    regions.forEach((el) => {
      el.addEventListener('click', () => select(owner.get(el.dataset.region), true));
      el.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse') hover(el.dataset.region, true);
      });
      el.addEventListener('pointerleave', () => hover(el.dataset.region, false));
    });
    picks.forEach((pick) => {
      const set = (on) => shapesOf(pick).forEach((el) => el.classList.toggle('is-hover', on));
      pick.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse') set(true);
      });
      pick.addEventListener('pointerleave', () => set(false));
    });

    /* Start on the first entry, without the entrance animation. */
    select(picks[0], false);
    panels.forEach((panel) => panel.classList.remove('is-in'));

    /* A few degrees of turn that follow the pointer. Mouse only. */
    const stage = root.querySelector('[data-ji-body-stage]');
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (stage && fine && !calm) {
      let frame = 0;
      stage.addEventListener('pointermove', (e) => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          const box = stage.getBoundingClientRect();
          const x = (e.clientX - box.left) / box.width - 0.5;
          const y = (e.clientY - box.top) / box.height - 0.5;
          stage.style.setProperty('--ry', (x * 10).toFixed(2) + 'deg');
          stage.style.setProperty('--rx', (y * -4).toFixed(2) + 'deg');
        });
      });
      stage.addEventListener('pointerleave', () => {
        stage.style.removeProperty('--ry');
        stage.style.removeProperty('--rx');
      });
    }

    /* Theme editor: selecting a block shows that area. */
    root.jiBodySelectBlock = (blockId) => {
      const pick = picks.find((p) => p.dataset.blockId === blockId);
      if (pick) select(pick, false);
    };
  };

  const init = () => document.querySelectorAll('[data-ji-body]').forEach(setup);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', (e) => {
    const root = e.target.closest('[data-ji-body]');
    if (root && root.jiBodySelectBlock) root.jiBodySelectBlock(e.detail.blockId);
  });
})();
